import { InputHTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';
import { useThemeStore } from '../../store/themeStore';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

const inputBaseByStyle = {
  bordered: 'px-4 py-2 border rounded-lg bg-white',
  underline: 'px-1 py-2 border-b-2 border-t-0 border-l-0 border-r-0 rounded-none bg-transparent',
  filled: 'px-4 py-2 border border-transparent rounded-lg bg-gray-100',
};

const inputFocusByStyle = {
  bordered: 'focus:border-primary-500 focus:ring-primary-500',
  underline: 'focus:border-primary-500 focus:ring-0',
  filled: 'focus:bg-white focus:border-primary-500 focus:ring-primary-500',
};

const inputErrorByStyle = {
  bordered: 'border-red-500 focus:border-red-500 focus:ring-red-500',
  underline: 'border-red-500 focus:border-red-500',
  filled: 'border-red-500 bg-red-50 focus:bg-white focus:border-red-500 focus:ring-red-500',
};

const inputNormalByStyle = {
  bordered: 'border-gray-300',
  underline: 'border-gray-300',
  filled: '',
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, className, id, ...props }, ref) => {
    const inputStyle = useThemeStore((s) => s.theme.layout.inputStyle);
    const inputId = id || label?.toLowerCase().replace(/\s/g, '-');

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={clsx(
            'w-full transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            inputBaseByStyle[inputStyle],
            props.disabled && 'opacity-50 cursor-not-allowed bg-gray-100',
            error
              ? inputErrorByStyle[inputStyle]
              : clsx(inputNormalByStyle[inputStyle], inputFocusByStyle[inputStyle]),
            className
          )}
          {...props}
        />
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
